import { Bell, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useWaiterCalls, WaiterCall } from "@/hooks/useWaiterCalls";
import { useWaiterCallNotifications } from "@/hooks/useWaiterCallNotifications";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface WaiterCallNotificationPanelProps {
  variant?: "header" | "inline";
}

export function WaiterCallNotificationPanel({ variant = "header" }: WaiterCallNotificationPanelProps) {
  const { pendingCalls, acknowledgeCall } = useWaiterCalls();
  const { newCalls, hasNewCalls, dismissCall, clearAllCalls } = useWaiterCallNotifications();

  // Merge real-time notifications with pending calls
  const allPendingCalls = pendingCalls;
  const callCount = allPendingCalls.length;

  if (variant === "inline") {
    if (callCount === 0) return null;

    return (
      <div className="space-y-2 w-full min-w-0">
        {allPendingCalls.map((call) => (
          <div
            key={call.id}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between p-2 sm:p-3 rounded-lg bg-destructive/10 border border-destructive/20 animate-pulse w-full min-w-0"
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
                <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-destructive" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-destructive text-sm sm:text-base truncate">
                  Mesa {call.table_number} chamando!
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(call.created_at), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => acknowledgeCall(call.id)}
              className="gap-1 shrink-0 w-full sm:w-auto"
            >
              <Check className="h-4 w-4" />
              Atender
            </Button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative",
            hasNewCalls && "text-destructive animate-pulse"
          )}
        >
          <Bell className="h-5 w-5" />
          {callCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]"
            >
              {callCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold">Chamadas de Garçom</h4>
          {callCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAllCalls}>
              Limpar
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[300px]">
          {callCount === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nenhuma chamada pendente</p>
            </div>
          ) : (
            <div className="divide-y">
              {allPendingCalls.map((call) => (
                <div
                  key={call.id}
                  className="p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                        <Bell className="h-4 w-4 text-destructive" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          Mesa {call.table_number}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(call.created_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => dismissCall(call.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => acknowledgeCall(call.id)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
